import React, { memo, useCallback, type ReactNode, useState } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';
import {
	ActionsWrapper,
	LeftActionsWrapper,
} from '@atlassian/jira-business-app-wrapper/src/ui/actions-wrapper/index.tsx';
import { useExpandCollapseAllItems } from '@atlassian/jira-business-expand-collapse-all-items-controller/src/index.tsx';
import { useHideDoneItems } from '@atlassian/jira-business-filters/src/controllers/hide-done-items/index.tsx';
import { Filters } from '@atlassian/jira-business-filters/src/ui/filters/index.tsx';
import { SimpleSearchField } from '@atlassian/jira-business-filters/src/ui/simple-search-field/index.tsx';
import AddPeopleWithAvatar from '@atlassian/jira-business-invite-people/src/ui/add-people-with-avatars/index.tsx';
import { JWMShareButton } from '@atlassian/jira-business-invite-people/src/ui/jwm-share-button/index.tsx';
import MoreMenu from '@atlassian/jira-business-more-menu/src/ui/more-menu/index.tsx';
import SettingsMenu from '@atlassian/jira-business-settings-menu/src/ui/settings-menu/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { getWillShowNav4 } from '@atlassian/jira-navigation-apps-sidebar-nav4-rollout-core/src/common/utils/get-will-show-nav4/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { integrationTypes } from '@atlassian/jira-share-button/src/constants.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useTimelineFeatures } from '../../../controllers/features-context/index.tsx';
import messages from './messages.tsx';
import TodayButton from './today-button/index.tsx';

export type ToolbarProps = {
	exportToImageButton: ReactNode;
};

const Toolbar = ({ exportToImageButton }: ToolbarProps) => {
	const [hideDoneItems, setHideDoneItems] = useHideDoneItems();
	const {
		hasShareButton,
		hasExportButton,
		inviteButtonInteraction,
		hasPresence,
		hasInviteButton,
		hasFilterCustomFields,
	} = useTimelineFeatures();

	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [hasFiltersApplied, setHasFiltersApplied] = useState(false);
	const [isSearching, setIsSearching] = useState(false);

	const shareButton = (
		<JWMShareButton
			shareTitle={messages.shareTitle}
			integrationType={integrationTypes.TIMELINE}
			shareContentType="timeline"
		/>
	);

	const searchBarLabelId = 'header-actions-search-field-label';

	const handleIsSearching = (text: String) => {
		setIsSearching(text.length > 0);
	};

	const handleHasFiltersApplied = (filtersCount: number) => {
		setHasFiltersApplied(filtersCount > 0);
	};

	const searchBar = (
		<>
			<VisuallyHidden id={searchBarLabelId}>{formatMessage(messages.searchLabel)}</VisuallyHidden>
			<SimpleSearchField
				placeholder={messages.simpleSearch}
				view="timeline"
				describedbyId={searchBarLabelId}
				onChange={handleIsSearching}
			/>
		</>
	);

	const addPeopleWithPresence = (
		<AddPeopleWithAvatar
			view="timeline"
			isPresenceEnabledInContext={hasPresence}
			hasInviteButton={hasInviteButton} // delete when cleaning up fg('visual-refresh')
			isOpenNewTabOnClick={inviteButtonInteraction === 'openNewTab'}
			allowCustomFields={hasFilterCustomFields}
		/>
	);

	const todayButton = <TodayButton />;

	const shareActionButton = hasShareButton && shareButton;

	const exportButton = hasExportButton && exportToImageButton;

	const filters = (
		<Filters
			view="timeline"
			allowCustomFields={hasFilterCustomFields}
			handleHasFiltersApplied={handleHasFiltersApplied}
		/>
	);

	const [
		{ hasExpandedAllItems, hasCollapsedAllItems },
		{ toggleExpandAllItems, collapseAllItems },
	] = useExpandCollapseAllItems({
		view: 'timeline',
	});

	const handleExpandCollapseAllItems = useCallback(
		(operation: 'collapse' | 'expand') => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'expandCollapseAll', {
				operation,
			});
			if (operation === 'collapse') {
				collapseAllItems();
			} else if (operation === 'expand') {
				toggleExpandAllItems();
			}
		},
		[collapseAllItems, createAnalyticsEvent, toggleExpandAllItems],
	);

	const visualRefeshEnabled = isVisualRefreshEnabled() && fg('visual-refresh_drop_2');

	const settingsMenu = visualRefeshEnabled && (
		<SettingsMenu
			hideDoneItems={Boolean(hideDoneItems)}
			onChangeHideDoneItems={setHideDoneItems}
			onExpandCollapseAllItems={handleExpandCollapseAllItems}
			hasExpandedAllItems={hasExpandedAllItems}
			hasCollapsedAllItems={hasCollapsedAllItems}
			isExpandCollapseButtonEnabled={!(isSearching || hasFiltersApplied)}
		/>
	);

	const moreMenu = (
		<MoreMenu
			hideDoneItems={Boolean(hideDoneItems)}
			onChangeHideDoneItems={setHideDoneItems}
			onExpandCollapseAllItems={handleExpandCollapseAllItems}
			hasExpandedAllItems={hasExpandedAllItems}
			hasCollapsedAllItems={hasCollapsedAllItems}
			isExpandCollapseButtonEnabled={!(isSearching || hasFiltersApplied)}
			hideItemsMenuOptions={visualRefeshEnabled}
		/>
	);

	return (
		<UFOSegment name="business-timeline-toolbar">
			<ActionsWrapper>
				<LeftActionsWrapper>
					{searchBar}
					{addPeopleWithPresence}
					{isVisualRefreshEnabled() && filters}
				</LeftActionsWrapper>

				<UnorderedListWrapper isVisualRefresh={isVisualRefreshEnabled()}>
					{!isVisualRefreshEnabled() && <ListItemWrapper>{todayButton}</ListItemWrapper>}
					{!isVisualRefreshEnabled() && !getWillShowNav4() && (
						<ListItemWrapper>{shareActionButton}</ListItemWrapper>
					)}
					<ListItemWrapper>{exportButton}</ListItemWrapper>
					{!isVisualRefreshEnabled() && <ListItemWrapper>{filters}</ListItemWrapper>}
					{visualRefeshEnabled ? (
						<ListItemWrapper>{settingsMenu}</ListItemWrapper>
					) : (
						<ListItemWrapper>{moreMenu}</ListItemWrapper>
					)}
				</UnorderedListWrapper>
			</ActionsWrapper>
		</UFOSegment>
	);
};

export default memo<ToolbarProps>(Toolbar);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UnorderedListWrapper = styled.ul<{ isVisualRefresh: boolean }>({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles
	padding: ({ isVisualRefresh }) => (isVisualRefresh ? '0 24px 0 0' : 0),
	margin: 0,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ListItemWrapper = styled.li({
	display: 'flex',
	alignItems: 'center',
	margin: 0,
});
